import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, X, Search, Camera } from 'lucide-react';
import { DataTable, Column } from '../common/DataTable';
import { ProductModal } from './ProductModal';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { BarcodeScanner } from '../common/BarcodeScanner';
import {
  useProductSearch,
  useFilterProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
} from '../../hooks/useProducts';
import { Product, ProductFormData } from '../../types/Product';
import { ProductFilterParams } from '../../services/productService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getErrorMessage } from '../../utils/errorMessages';

export const ProductList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [sortBy, setSortBy] = useState<'id' | 'quantity' | 'unitPrice' | 'retailPrice'>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  
  // Filter states
  const [filterBrand, setFilterBrand] = useState('');
  const [filterColor, setFilterColor] = useState('');
  const [filterStockStatus, setFilterStockStatus] = useState<'all' | 'inStock' | 'lowStock' | 'outOfStock'>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50; // Sayfa başına 50 ürün

  const { data: searchResults } = useProductSearch(searchTerm);
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  // Check if any filter is active
  const hasActiveFilters = filterBrand || filterColor || filterStockStatus !== 'all';

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filterBrand, filterColor, filterStockStatus]);

  // Prepare filter parameters for API call (with pagination)
  // Backend uses /api/products endpoint with query params for filtering
  const filterParams = useMemo(() => {
    const params: ProductFilterParams = {
      page: currentPage,
      size: pageSize,
      sortBy,
      sortDir,
    };
    // Add filter parameters only if they have values
    if (filterBrand && filterBrand.trim()) params.brand = filterBrand.trim();
    if (filterColor && filterColor.trim()) params.color = filterColor.trim();
    if (filterStockStatus !== 'all') {
      params.stockStatus = filterStockStatus;
    }
    return params;
  }, [filterBrand, filterColor, filterStockStatus, currentPage, pageSize, sortBy, sortDir]);

  // Always use filter endpoint - it works even without filter parameters
  const { data: filteredPage, isLoading: isFilterLoading } = useFilterProducts(filterParams);

  // Extra request for total count when filters are active (for "x ürün bulundu")
  const countParams = useMemo(
    () => (hasActiveFilters ? { ...filterParams, page: 0, size: 1 } : null),
    [hasActiveFilters, filterParams]
  );
  const { data: filterCountPage } = useFilterProducts(countParams);
  const filterTotalCount = filterCountPage?.totalElements ?? filteredPage?.totalElements ?? 0;

  // Get unique brands and colors for filter dropdowns (from filtered page)
  const firstPageProducts = useMemo(() => {
    return filteredPage?.content || [];
  }, [filteredPage?.content]);

  const uniqueBrands = useMemo(() => {
    const brands = new Set<string>();
    firstPageProducts.forEach((p) => {
      if (p.brand) brands.add(p.brand);
    });
    return Array.from(brands).sort();
  }, [firstPageProducts]);

  const uniqueColors = useMemo(() => {
    const colors = new Set<string>();
    firstPageProducts.forEach((p) => {
      if (p.color) colors.add(p.color);
    });
    return Array.from(colors).sort();
  }, [firstPageProducts]);

  // Determine which products to display
  // Always use backend pagination - no client-side filtering to ensure consistent page sizes
  const displayProducts = useMemo(() => {
    if (searchTerm && searchResults) {
      // Use search results when searching (no pagination for search)
      const sorted = [...searchResults];
      if (sortBy === 'quantity' || sortBy === 'unitPrice' || sortBy === 'retailPrice') {
        sorted.sort((a, b) => {
          const av = (a as any)[sortBy] ?? 0;
          const bv = (b as any)[sortBy] ?? 0;
          return sortDir === 'asc' ? av - bv : bv - av;
        });
      }
      return sorted;
    } else if (filteredPage) {
      // Always use filtered page results directly from backend
      // Backend handles all filtering including pagination
      return filteredPage.content || [];
    } else {
      return [];
    }
  }, [searchTerm, searchResults, filteredPage, sortBy, sortDir]);

  // Determine pagination info from filtered page
  const totalPages = filteredPage?.totalPages || 0;
  const totalElements = filteredPage?.totalElements || 0;

  // Clear all filters
  const clearFilters = () => {
    setFilterBrand('');
    setFilterColor('');
    setFilterStockStatus('all');
    setCurrentPage(0);
  };

  const columns: Column<Product>[] = [
    { id: 'id', label: 'ID', minWidth: 50 },
    { id: 'name', label: 'Ürün Adı', minWidth: 150 },
    { id: 'color', label: 'Renk', minWidth: 100 },
    { id: 'quantity', label: 'Miktar', minWidth: 80, align: 'right', sortable: true, sortKey: 'quantity' },
    { id: 'brand', label: 'Marka', minWidth: 100 },
    { id: 'code', label: 'Barkod', minWidth: 100 },
    {
      id: 'purchaseDate',
      label: 'Alım Tarihi',
      minWidth: 120,
      format: (value) => value ? formatDate(value as string) : '-',
    },
    {
      id: 'unitPrice',
      label: 'Alış Fiyatı',
      minWidth: 100,
      align: 'right',
      sortable: true,
      sortKey: 'unitPrice',
      format: (value) => formatCurrency(value as number),
    },
    {
      id: 'retailPrice',
      label: 'Satış Fiyatı',
      minWidth: 100,
      align: 'right',
      sortable: true,
      sortKey: 'retailPrice',
      format: (value) => formatCurrency(value as number),
    },
    { id: 'description', label: 'Açıklama', minWidth: 200 },
  ];

  const handleAdd = () => {
    setSelectedProduct(undefined);
    setModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const handleRowClick = (product: Product) => {
    setProductToDelete(product);
  };

  const handleDeleteClick = () => {
    if (productToDelete) {
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (productToDelete) {
      try {
        await deleteProduct.mutateAsync(productToDelete.id);
        setToast({ open: true, message: 'Ürün başarıyla silindi', severity: 'success' });
        setDeleteDialogOpen(false);
        setProductToDelete(null);
      } catch (error) {
        setToast({ open: true, message: 'Ürün silinirken hata oluştu', severity: 'error' });
      }
    }
  };

  const handleSave = async (data: ProductFormData) => {
    try {
      if (selectedProduct) {
        await updateProduct.mutateAsync({ id: selectedProduct.id, data });
        setToast({ open: true, message: 'Ürün başarıyla güncellendi', severity: 'success' });
      } else {
        await createProduct.mutateAsync(data);
        setToast({ open: true, message: 'Ürün başarıyla eklendi', severity: 'success' });
      }
      setModalOpen(false);
      setSelectedProduct(undefined);
    } catch (error) {
      setToast({ open: true, message: getErrorMessage(error, 'İşlem sırasında hata oluştu'), severity: 'error' });
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Arama yapıldığında sayfayı sıfırla
    if (value) {
      setCurrentPage(0);
    }
  };

  const handleBarcodeScan = (barcode: string) => {
    setSearchTerm(barcode);
    setCurrentPage(0);
  };

  const handleSortChange = (columnId: string, direction: 'asc' | 'desc') => {
    setSortBy((columnId as 'id' | 'quantity' | 'unitPrice' | 'retailPrice') || 'id');
    setSortDir(direction);
    setCurrentPage(0);
  };

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {isFilterLoading === false && !filteredPage && !searchTerm && (
        <div className="card p-4 bg-warning/10 border-warning/20 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
          <p className="text-sm text-foreground-secondary">
          Backend bağlantısı kurulamadı. Veriler yüklenemiyor.
          </p>
        </div>
      )}

      {/* Search, Filters and Actions */}
      <div className="space-y-3 md:space-y-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none">
              <Search className="w-4 h-4 md:w-5 md:h-5 text-foreground-tertiary" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Ürün adı veya barkod"
              className="input pl-9 md:pl-11 pr-10 md:pr-12 w-full text-sm md:text-base"
            />
            <button
              onClick={() => setScannerOpen(true)}
              className="absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center hover:opacity-70 transition-opacity"
              type="button"
              title="Barkod Tara"
            >
              <Camera className="w-4 h-4 md:w-5 md:h-5 text-accent" />
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary flex items-center gap-1.5 md:gap-2 whitespace-nowrap text-xs md:text-sm px-2 md:px-4 py-2 ${showFilters ? 'bg-accent/20 border-accent' : ''}`}
            >
              <Filter className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span className="hidden sm:inline">Filtrele</span>
              {hasActiveFilters && (
                <span className="px-1.5 py-0.5 bg-accent text-white text-xs rounded-full">
                  {[filterBrand, filterColor, filterStockStatus !== 'all' ? 1 : 0].filter(Boolean).length}
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn-secondary flex items-center gap-1.5 md:gap-2 whitespace-nowrap text-xs md:text-sm px-2 md:px-4 py-2"
              >
                <X className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Temizle</span>
              </button>
            )}
            <button
              onClick={handleAdd}
              className="btn-primary flex items-center gap-1.5 md:gap-2 text-xs md:text-sm px-2 md:px-4 py-2"
            >
              <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" />
              <span className="hidden sm:inline">Yeni</span>
            </button>
            {productToDelete && (
              <button
                onClick={handleDeleteClick}
                className="btn-danger flex items-center gap-1.5 md:gap-2 text-xs md:text-sm px-2 md:px-4 py-2"
                disabled={!productToDelete}
              >
                <Trash2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Sil</span>
              </button>
            )}
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="card p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Brand Filter */}
              <div>
                <label htmlFor="filter-brand" className="block text-sm font-medium text-foreground-secondary mb-2">
                  Marka
                </label>
                <input
                  id="filter-brand"
                  type="text"
                  value={filterBrand}
                  onChange={(e) => setFilterBrand(e.target.value)}
                  placeholder="Marka ara..."
                  className="input"
                  list="brands-list"
                />
                <datalist id="brands-list">
                  {uniqueBrands.map((brand) => (
                    <option key={brand} value={brand} />
                  ))}
                </datalist>
              </div>

              {/* Color Filter */}
              <div>
                <label htmlFor="filter-color" className="block text-sm font-medium text-foreground-secondary mb-2">
                  Renk
                </label>
                <input
                  id="filter-color"
                  type="text"
                  value={filterColor}
                  onChange={(e) => setFilterColor(e.target.value)}
                  placeholder="Renk ara..."
                  className="input"
                  list="colors-list"
                />
                <datalist id="colors-list">
                  {uniqueColors.map((color) => (
                    <option key={color} value={color} />
                  ))}
                </datalist>
              </div>

              {/* Stock Status Filter */}
              <div>
                <label htmlFor="filter-stock-status" className="block text-sm font-medium text-foreground-secondary mb-2">
                  Stok Durumu
                </label>
                <select
                  id="filter-stock-status"
                  value={filterStockStatus}
                  onChange={(e) => setFilterStockStatus(e.target.value as 'all' | 'inStock' | 'lowStock' | 'outOfStock')}
                  className="input"
                >
                  <option value="all">Tümü</option>
                  <option value="inStock">Stokta Var (≥10)</option>
                  <option value="lowStock">Düşük Stok (1-9)</option>
                  <option value="outOfStock">Tükendi (0)</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Info */}
      {(hasActiveFilters || searchTerm) && (
        <div className="text-sm text-foreground-secondary">
          {hasActiveFilters && !searchTerm ? `${filterTotalCount} ürün bulundu` : `${displayProducts.length} ürün bulundu`}
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        rows={displayProducts}
        loading={isFilterLoading}
        onRowDoubleClick={handleEdit}
        onRowClick={handleRowClick}
        getRowId={(row) => row.id}
        selectedRowId={productToDelete?.id}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={handleSortChange}
      />

      {/* Pagination - Arama yapılmadığında göster */}
      {!searchTerm && filteredPage && totalPages > 1 && (
        <div className="card p-4 overflow-x-hidden max-w-full">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0">
            <div className="text-xs md:text-sm text-foreground-secondary whitespace-nowrap">
              Toplam {totalElements} ürün - Sayfa {currentPage + 1} / {totalPages}
            </div>
            <div className="flex items-center gap-1 md:gap-2 w-full md:w-auto overflow-x-auto md:overflow-x-visible max-w-full">
              {/* En Başa Git */}
              <button
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0 || isFilterLoading}
                className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                title="En başa git"
              >
                <ChevronsLeft className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </button>
              
              {/* Önceki Sayfa */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0 || isFilterLoading}
                className="flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium whitespace-nowrap"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Önceki</span>
              </button>
              
              {/* Sayfa Numaraları */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (currentPage < 3) {
                    pageNum = i;
                  } else if (currentPage > totalPages - 4) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={isFilterLoading}
                      className={`flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg text-xs md:text-sm font-medium transition-smooth ${
                        currentPage === pageNum
                          ? 'bg-accent text-white'
                          : 'bg-background-tertiary text-foreground hover:bg-background-secondary border border-border'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>
              
              {/* Sonraki Sayfa */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1 || isFilterLoading}
                className="flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed text-xs md:text-sm font-medium whitespace-nowrap"
              >
                <span className="hidden sm:inline">Sonraki</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              
              {/* Sona Git */}
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage >= totalPages - 1 || isFilterLoading}
                className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-lg bg-background-tertiary text-foreground border border-border hover:bg-background-secondary transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                title="Sona git"
              >
                <ChevronsRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <ProductModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedProduct(undefined);
        }}
        onSave={handleSave}
        product={selectedProduct}
        loading={createProduct.isPending || updateProduct.isPending}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setProductToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Ürün Sil"
        message={`"${productToDelete?.name}" ürününü silmek istediğinize emin misiniz?`}
        loading={deleteProduct.isPending}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
        severity={toast.severity}
      />

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScan}
      />
    </div>
  );
};
